import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { publicApi, setApiRole } from "../api/api";
import styles from "../styles/LandingPage.module.css";
import productImage from "../assets/images/product-image.jpg";
import limitedoffer from "../assets/images/limited-offer.gif";
import { FaWhatsapp } from "react-icons/fa";
import VideoPlayer from "../components/VideoPlayer";
import thumbnail from "../assets/images/astroshubh-thumbnail.jpg";
import TimePicker from "../components/TimePicker";
import SpinnerOverlay from "../components/SpinnerOverlay";
import { postCreateOrderWithRetry } from "../utils/orderApi";
import DateOfBirthField from "../components/DateOfBirthField";
import UnknownBirthTimeCheckbox from "../components/UnknownBirthTimeCheckbox";
import ReviewModal from "../components/ReviewModal";
import { getLandingImages, LANDING_IMAGE_TAG } from "../utils/landingImages";
import PlaceOfBirthField from "../components/PlaceOfBirthField";
import {
  getQueryParam,
  setCookie,
  areCookiesEnabled,
} from "../utils/fbclid";
import { trackLandingPageVisit, trackLeadEvent } from "../utils/metaPageTracking";
import useLandingTracking from "../hooks/useLandingTracking";
import getDeviceContext from "../utils/deviceContext";
import CriticalErrorHandler from "../utils/criticalErrorHandler";
import criticalLogger from "../utils/criticalLogger";
import RecentOrderToast from "../components/RecentOrderToast";
import FAQ from "../components/FAQ";
import { useCheckoutConfig } from "../checkout";
import { FAKE_WHATSAPP_NUMBERS } from "../config/constants";
import {
  availableSlots,
  firstBookableDate,
  lastBookableDate,
  isTodayClosed,
  slotLabel,
} from "../config/consultationSlots";
// REMOVED: razorpayUtils, FallbackPaymentModal, saveCheckoutSession imports — only used by
// the payment logic that moved to CommonPaymentPage.jsx.

// Success Modal Component
const SuccessModal = ({ show, message, onClose, title = "Success" }) => {
  if (!show) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        background: "rgba(255,255,255,0.75)",
        zIndex: 10000,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
        transition: "opacity 0.2s",
      }}
    >
      <div
        style={{
          border: "6px solid #c8e6c9",
          borderTop: "6px solid #4caf50",
          borderRadius: "50%",
          width: 60,
          height: 60,
          animation: "spin 1s linear infinite",
          marginBottom: 18,
        }}
      />
      <div
        style={{
          fontSize: "18px",
          fontWeight: 600,
          color: "#4caf50",
          textAlign: "center",
          marginBottom: "12px",
        }}
      >
        ✅ {title}
      </div>
      <div
        style={{
          fontSize: "16px",
          fontWeight: 500,
          color: "#3E2723",
          textAlign: "center",
          lineHeight: 1.5,
        }}
      >
        {message}
      </div>
      <style>{`@keyframes spin { 0% { transform: rotate(0deg);} 100% { transform: rotate(360deg);} }`}</style>
    </div>
  );
};

// Swipe gesture refs (must be at top level, outside component)
const touchStartX = React.createRef();
const touchEndX = React.createRef();
let mouseDownX = null;
let mouseUpX = null;

const LandingPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { checkoutMode } = useCheckoutConfig();
  const [currentImage, setCurrentImage] = useState(0);
  // REMOVED: paymentIntervalRef/paymentTimeoutRef — only ever set inside handlePayment
  // (moved to CommonPaymentPage.jsx, which has its own copies for the same purpose).
  // Swipe gesture refs (must be at top level)
  // ...existing code...
  const [product, setProduct] = useState(null);
  const [productReviews, setProductReviews] = useState([]);
  const [productLabels, setProductLabels] = useState([]);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    dob: "",
    tob: "",
    place: "",
    whatsapp: "",
    gender: "",
    language: "",
    remark: "",
    termsAccepted: true,
    selectedAddons: [], // Array of selected addon IDs
    consultation_date: null,
    consultation_slot: null,
    consultation_problem_type: null,
    consultation_language: "",
    customFields: {}, // Store custom field values for main product {fieldId: value}
    addonCustomFields: {} // Store custom field values per addon {addonId: {fieldId: value}}
  });
  const [remarkCharCount, setRemarkCharCount] = useState(0);
  const [formError, setFormError] = useState("");
  const [placeValidationError, setPlaceValidationError] = useState("");
  // "Don't know my exact time of birth": ticking it fills 12:00 PM; the time the user had
  // entered before is kept so unticking can put it back.
  const [unknownTob, setUnknownTob] = useState(false);
  const previousTobRef = React.useRef("");
  const [loading, setLoading] = useState(false);
  const isSubmittingRef = React.useRef(false);
  const [loadingMessage, setLoadingMessage] = useState(
    "Creating your order..."
  );
  const [leadId, setLeadId] = useState(null);
  const [termsError, setTermsError] = useState("");
  const [productError, setProductError] = useState("");
  const [successModal, setSuccessModal] = useState({
    show: false,
    message: "",
    title: "Success",
  });

  // Recent orders toast state
  const [recentOrders, setRecentOrders] = useState([]);
  const [currentToastIndex, setCurrentToastIndex] = useState(0);
  const [showToast, setShowToast] = useState(false);

  // REMOVED (moved to CommonPaymentPage.jsx): paymentLoading, walletLoading, balance,
  // errorModal, otpModal, resendDisabled, resendTimer, fallbackModal — all purely
  // payment-flow state, confirmed zero remaining callers in this file after the move.


  // First-party visitor tracking — re-fires visit ping + restarts heartbeat whenever
  // the product changes so the dashboard shows accurate per-product live counts.
  const visitorId = useLandingTracking(id);

  // REMOVED: browserWarning state — only ever set inside handlePayment (moved to
  // CommonPaymentPage.jsx) and never actually read in this file's own render.

  // ── Consultation slot clock ──
  // Slot filtering is anchored to the server clock (converted to IST), not the
  // customer's device, so a phone with a wrong date can't surface unbookable
  // slots. skew = server epoch − device epoch, captured when /products/:id
  // responds; 0 until then, which degrades to the device clock.
  const [serverClockSkewMs, setServerClockSkewMs] = useState(0);
  const [clockTickMs, setClockTickMs] = useState(() => Date.now());
  const effectiveNowMs = clockTickMs + serverClockSkewMs;
  const [slotNotice, setSlotNotice] = useState("");

  // Re-evaluate every minute so a page left open stops offering passed slots.
  useEffect(() => {
    const id = setInterval(() => setClockTickMs(Date.now()), 60000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {

    // Initialise Meta Pixel and capture ?fbclid= attribution
    const pixelId = "780498105149088";
    trackLandingPageVisit(pixelId);

    const fbclid = getQueryParam("fbclid");
    const utm_source = getQueryParam("utm_source");

    if (fbclid) {
      // store for later use in localStorage (works even if cookies disabled)
      localStorage.setItem("fbclid", fbclid);

      // Try to create _fbc cookie in the same format Meta expects: fb.1.<timestamp>.<fbclid>
      const ts = Math.floor(Date.now() / 1000);
      const fbc = `fb.1.${ts}.${fbclid}`;

      const cookieSet = setCookie("_fbc", fbc);
      if (!cookieSet) {
        // Fallback: store in localStorage with a different key
        localStorage.setItem("_fbc_fallback", fbc);
        console.log(
          "Cookies disabled - storing Facebook tracking data in localStorage"
        );
      }
    }

    if (utm_source === "facebook") {
      // optional marker
      localStorage.setItem("utm_source", "facebook");
    }

    // Check cookie support and log for debugging
    if (!areCookiesEnabled()) {
      console.warn(
        "Cookies are disabled in this browser. Facebook pixel tracking may be limited."
      );
    }

    // Debug iPhone tracking if needed (remove in production)
    // debugiPhoneTracking();
  }, []);

  // Flush pending critical logs before component unmounts
  useEffect(() => {
    return () => {
      criticalLogger.flushNow();
    };
  }, []);

  useEffect(() => {
    publicApi
      .get(`/products/${id}`, { params: { image_tag: LANDING_IMAGE_TAG } })
      .then((res) => {
        // Anchor slot filtering to the server clock — see effectiveNowMs
        const serverNowMs = Number(res.data?.server_now_ms);
        if (Number.isFinite(serverNowMs) && serverNowMs > 0) {
          setServerClockSkewMs(serverNowMs - Date.now());
        }
        setProduct(res.data.data);
        // Website-tagged images, main first (falls back to all images when none are tagged)
        const { images: landingImages, mainIndex: mainIdx } = getLandingImages(res.data.data);
        setCurrentImage(mainIdx);

        // Preload the LCP image as soon as the URL is known so the
        // browser starts fetching it before the component re-renders.
        const lcpImageUrl =
          landingImages.length > 0
            ? landingImages[mainIdx]
            : res.data.data?.image;
        if (lcpImageUrl && typeof document !== 'undefined') {
          const existing = document.querySelector(`link[rel="preload"][href="${lcpImageUrl}"]`);
          if (!existing) {
            const preload = document.createElement('link');
            preload.rel = 'preload';
            preload.as = 'image';
            preload.href = lcpImageUrl;
            preload.fetchPriority = 'high';
            document.head.appendChild(preload);
          }
        }
        // Fetch reviews and labels for this product
        if (res.data.data?.id) {
          publicApi.get(`/product-reviews/${res.data.data.id}`)
            .then((reviewRes) => {
              if (reviewRes.data.status === 'success') {
                setProductReviews(reviewRes.data.data || []);
              }
            })
            .catch((err) => {
              console.error("Error loading reviews:", err);
            });
          
          // Fetch product labels
          publicApi.get(`/products/${res.data.data.id}/labels`)
            .then((labelsRes) => {
              if (labelsRes.data.success) {
                setProductLabels(labelsRes.data.data || []);
              }
            })
            .catch((err) => {
              console.error("Error loading labels:", err);
            });
        }
      })
      .catch((err) => {
        console.error("Error loading product:", err);
        // Log critical API error
        CriticalErrorHandler.reportApiError(`/products/${id}`, err);
        setProductError("Please refresh the page or try again after some time.");
      });
  }, [id]);

  // Fetch and display recent orders toast notifications
  useEffect(() => {
    const fetchRecentOrders = async () => {
      try {
        const response = await publicApi.get('/recent-orders');
        if (response.data.status === 'success' && response.data.data.length > 0) {
          setRecentOrders(response.data.data);
          // Show first toast after 1.5 seconds
          setTimeout(() => {
            setShowToast(true);
            setCurrentToastIndex(0);
          }, 1500);
        }
      } catch (error) {
        console.error('Failed to fetch recent orders:', error);
      }
    };
    
    fetchRecentOrders();
  }, []);

  // Override body styles for landing page only
  useEffect(() => {
    document.body.classList.add('landing-page-body');
    
    return () => {
      document.body.classList.remove('landing-page-body');
    };
  }, []);

  // Handle showing next toast
  const handleToastClose = () => {
    setShowToast(false);
    
    // Show next toast if available
    if (currentToastIndex + 1 < recentOrders.length) {
      setTimeout(() => {
        setCurrentToastIndex(prev => prev + 1);
        setShowToast(true);
      }, 3000); // 3 seconds gap between toasts
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    // Picking a time by hand means the user does know it
    if (name === "tob") setUnknownTob(false);
    if (name === "remark") {
      const trimmedValue = value.slice(0, 240);
      setFormData((prev) => ({ ...prev, remark: trimmedValue }));
      setRemarkCharCount(trimmedValue.length);
    } else if (name === "whatsapp") {
      // Only allow numbers for WhatsApp field
      const numericValue = value.replace(/[^0-9]/g, "");
      setFormData((prev) => ({ ...prev, [name]: numericValue }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
    setFormError("");
  };

  const UNKNOWN_TOB_DEFAULT = "12:00"; // 12:00 PM

  const handleUnknownTobChange = (checked) => {
    setUnknownTob(checked);
    setFormData((prev) => {
      if (checked) {
        previousTobRef.current = prev.tob;
        return { ...prev, tob: UNKNOWN_TOB_DEFAULT };
      }
      return { ...prev, tob: previousTobRef.current };
    });
    setFormError("");
  };

  const handlePlaceChange = (val) => {
    setFormData((prev) => ({ ...prev, place: val }));
    // Clear form error when user changes place
    if (formError) setFormError("");
  };

  const handlePlaceValidationError = (error) => {
    setPlaceValidationError(error);
  };
  // REMOVED: fetchBalance — moved to CommonPaymentPage.jsx (its only caller, handlePayment,
  // moved there too).
  const validateForm = () => {
    // 1. Fullname only accept alphabets and space
    if (!formData.name.trim()) return "Name is required.";
    if (!/^[a-zA-Z ]+$/.test(formData.name.trim()))
      return "Name can only contain letters and spaces.";
    // 2. Date of birth cannot be future date - check if needed for main product OR addons
    if (needsProfileFields || addonNeedsProfileFields) {
      if (!formData.dob) return "Date of Birth is required.";
      if (!/^\d{4}-\d{2}-\d{2}$/.test(formData.dob))
        return "Birth year must be a 4-digit year.";
      const today = new Date();
      const dobDate = new Date(formData.dob);
      if (dobDate > today) return "Date of Birth cannot be in the future.";
      const minDobDate = new Date(today.getFullYear() - 100, today.getMonth(), today.getDate());
      if (dobDate < minDobDate)
        return "Date of Birth cannot be more than 100 years ago.";
      if (!formData.tob) return "Time of Birth is required.";
      
      // Validate time format (HH:MM)
      const timePattern = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
      if (!timePattern.test(formData.tob)) {
        return "Please enter time in HH:MM format (e.g., 14:30)";
      }
      
      if (!formData.place.trim()) return "Place of Birth is required.";
      // Check if place was selected from suggestions (not just manually typed)
      if (placeValidationError) return placeValidationError;
      
      if (!formData.gender) return "Gender is required.";
    }
    
    // 3. Validate consultation-specific fields — main product or selected addon
    if (needsConsultationFields) {
      if (!formData.consultation_date) {
        return `Consultation Date is required`;
      }
      if (!formData.consultation_slot) {
        return `Consultation Slot is required`;
      }
      if (!formData.consultation_problem_type) {
        return `Problem Type is required`;
      }
      if (!formData.consultation_language || formData.consultation_language === '') {
        return `Consultation Language is required`;
      }
    }
    if (!formData.whatsapp.trim()) return "WhatsApp number is required.";
    if (!/^\d{10}$/.test(formData.whatsapp.trim()))
      return "Enter a valid 10-digit WhatsApp number.";

    // Fake number validation
    if (FAKE_WHATSAPP_NUMBERS.includes(formData.whatsapp.trim())) {
      return "Please enter a valid WhatsApp number";
    }
    // All digits same
    if (/^(\d)\1{9}$/.test(formData.whatsapp.trim())) {
      return "Please enter a valid WhatsApp number";
    }
    // Number starts with 123
    if (/^123\d{7}$/.test(formData.whatsapp.trim())) {
      return "Please enter a valid WhatsApp number";
    }
     // Number starts with 12
    if (/^12\d{8}$/.test(formData.whatsapp.trim())) {
      return "Please enter a valid WhatsApp number";
    }
    
    // 4. Validate custom fields if product has them
    if (product.user_input_fields && Array.isArray(product.user_input_fields)) {
      for (const fieldDef of product.user_input_fields) {
        const value = formData.customFields[fieldDef.id];
        
        if (fieldDef.required && (!value || (typeof value === 'string' && !value.trim()))) {
          return `${fieldDef.label} is required`;
        }
        
        if (value && typeof value === 'string' && value.length > 500) {
          return `${fieldDef.label} must not exceed 500 characters`;
        }
        
        if (fieldDef.type === 'dropdown' && value && fieldDef.options && !fieldDef.options.includes(value)) {
          return `Please select a valid option for ${fieldDef.label}`;
        }
      }
    }
    
    return null;
  };

  // Store existing lead/order in state to prevent duplicate creation
  const [existingLead, setExistingLead] = useState(null);
  const [existingOrder, setExistingOrder] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setFormError("");
    setTermsError("");

    if (!formData.termsAccepted) {
      setTermsError("You must accept the Terms & Conditions to proceed.");
      isSubmittingRef.current = false;
      return;
    }

    const err = validateForm();
    if (err) {
      setFormError(err);
      isSubmittingRef.current = false;
      return;
    }

    setLoading(true);
    setLoadingMessage("Preparing your order...");
    const deviceContext = getDeviceContext();
    
    try {
      // Helper function to transform custom fields to include labels
      const transformCustomFields = (customFieldsData, fieldDefinitions) => {
        if (!customFieldsData || Object.keys(customFieldsData).length === 0 || !fieldDefinitions) {
          return null;
        }
        
        const transformed = {};
        fieldDefinitions.forEach(fieldDef => {
          const value = customFieldsData[fieldDef.id];
          if (value !== undefined && value !== null && value !== '') {
            transformed[fieldDef.id] = {
              label: fieldDef.label,
              value: value
            };
          }
        });
        
        return Object.keys(transformed).length > 0 ? transformed : null;
      };

      // Build complete order snapshot with main product and addons
      // ALWAYS build this to detect changes in addon selection
      const orderItems = [];
      
      // Transform main product custom fields
      const mainProductCustomFields = transformCustomFields(
        formData.customFields,
        product.user_input_fields
      );
      
      // Add main product
      orderItems.push({
        item_id: product.id,
        item_name: product.name,
        item_price: parseFloat(product.price),
        item_type: "main",
        service_id: product.service_id,
        service_name: product.service || product.service_name || "Service",
        custom_fields: mainProductCustomFields
      });
      
      // Add selected addons with their custom fields
      if (formData.selectedAddons.length > 0 && product.available_addons) {
        formData.selectedAddons.forEach(addonId => {
          const addon = product.available_addons.find(a => a.id === addonId);
          if (addon) {
            // Transform addon custom fields
            const addonCustomFields = transformCustomFields(
              formData.addonCustomFields[addonId],
              addon.user_input_fields
            );
            
            orderItems.push({
              item_id: addon.id,
              item_name: addon.name,
              item_price: parseFloat(addon.price),
              item_type: "addon",
              service_id: addon.service_id,
              service_name: addon.service || "Service",
              custom_fields: addonCustomFields
            });
          }
        });
      }
      
      // Create complete order snapshot
      const orderSnapshot = {
        order_items: orderItems,
        total_amount: totalAmount,
        snapshot_date: new Date().toISOString()
      };

      // Prepare consultation data (null only when nothing in the order is a consultation)
      const consultationData = needsConsultationFields ? {
        consultation_date: formData.consultation_date || null,
        consultation_slot: formData.consultation_slot || null,
        consultation_problem_type: formData.consultation_problem_type || null,
        consultation_language: formData.consultation_language || formData.language || "Hindi",
      } : {
        consultation_date: null,
        consultation_slot: null,
        consultation_problem_type: null,
        consultation_language: null,
      };

      console.log("📋 Consultation data prepared:", {
        isConsultationProduct,
        hasConsultationAddon,
        consultationData
      });

      // Check if lead/order already exist and if any data changed
      if (existingLead && existingOrder) {
        // Create complete snapshot of all form data (except mobile which is identifier)
        const currentFormSnapshot = {
          name: formData.name,
          dob: formData.dob,
          tob: formData.tob,
          place: formData.place,
          gender: formData.gender,
          language: formData.language,
          remark: formData.remark,
          selectedAddons: formData.selectedAddons.sort(),
          consultation_date: formData.consultation_date,
          consultation_slot: formData.consultation_slot,
          consultation_problem_type: formData.consultation_problem_type,
          consultation_language: formData.consultation_language,
          orderSnapshot: orderSnapshot,
          totalAmount: totalAmount
        };
        
        const currentFormData = JSON.stringify(currentFormSnapshot);
        const existingFormData = existingOrder.formSnapshot || '';
        
        if (currentFormData === existingFormData) {
          // No changes detected - reuse existing order
          console.log("✅ Reusing existing order - no changes detected");
          setLoading(false);
          setSuccessModal({
            show: true,
            message: "Using existing order. Proceeding to payment...",
            title: "Order Ready",
          });
          setTimeout(() => {
            setSuccessModal({ show: false, message: "", title: "Success" });
            navigate("/order-payment", {
              state: {
                orderId: existingOrder.orderId,
                userId: existingOrder.userId,
                amount: existingOrder.amt,
                leadId: existingLead.leadId,
                customerName: formData.name,
                customerContact: formData.whatsapp,
                product,
                checkoutMode,
              },
            });
          }, 1000);
          return;
        } else {
          // Data changed - update order
          console.log("🔄 Order data changed - updating order", {
            changes_detected: true
          });
          
          // Previously cleared a stale Razorpay session here when the amount changed —
          // no longer needed: rechargeResState now lives only in CommonPaymentPage.jsx,
          // which mounts fresh (via navigate) on every payment attempt, so there's no
          // persisted session across order updates left to go stale.
          setLoadingMessage("Updating your order...");
          
          // Call backend to update order items
          const updateRes = await postCreateOrderWithRetry({
            mobile: formData.whatsapp,
            profile: {
              full_name: formData.name,
              dob: formData.dob,
              birth_time: formData.tob,
              birth_place: formData.place,
              gender: formData.gender,
              mobile: formData.whatsapp,
            },
            order_id: existingOrder.orderId, // Pass existing order ID for update
            lead_id: existingLead.leadId,
            product_id: product?.id || null,
            service_id: product?.service_id || null,
            report_prefered_lang: formData.language || "Hindi",
            amount: totalAmount,
            remark: formData.remark || "",
            selected_addons: orderSnapshot,
            ...consultationData,
            is_profile_required: needsProfileFields || addonNeedsProfileFields,
            skip_otp: true,
            deviceContext,
          });
          
          if (updateRes.data.status === "success") {
            const { order } = updateRes.data.data;
            // Update existing order state with complete form snapshot
            setExistingOrder({
              ...order,
              formSnapshot: currentFormData
            });
            
            console.log("✅ Order items updated successfully");
            setLoading(false);
            setSuccessModal({
              show: true,
              message: "Order updated successfully. Proceeding to payment...",
              title: "Order Updated",
            });
            setTimeout(() => {
              setSuccessModal({ show: false, message: "", title: "Success" });
              navigate("/order-payment", {
                state: {
                  orderId: order.orderId,
                  userId: order.userId,
                  amount: order.amt,
                  leadId: existingLead.leadId,
                  customerName: formData.name,
                  customerContact: formData.whatsapp,
                  product,
                  checkoutMode,
                },
              });
            }, 1000);
            return;
          } else {
            setFormError(updateRes.data.message || "Failed to update order.");
            setLoading(false);
            return;
          }
        }
      }
      
      // No existing order - create new lead and order
      setLoadingMessage("Creating your order...");
      
      // Create lead first
      const leadres = await publicApi.post("/create-lead", {
        mobile: formData.whatsapp,
        profile: {
          full_name: formData.name,
          dob: formData.dob,
          birth_time: formData.tob,
          birth_place: formData.place,
          gender: formData.gender,
        },
        product_id: product?.id || null,
        service_id: product?.service_id || null,
        amount: totalAmount,
        report_prefered_lang: formData.language || "Hindi",
        remark: formData.remark,
        selected_addons: orderSnapshot,
        visitor_id: visitorId,  // first-party tracking
        utm_source: getQueryParam("utm_source"), // lead_source tagged 'wa-agent' only when this matches, else stays NULL
        ...consultationData,
      });

      const leadId = leadres.data.data.leadId;
      setLeadId(leadId);
      setExistingLead({ leadId });

      // Track Meta Lead event
      trackLeadEvent(product);

      // Create order and user directly without OTP
      const payload = {
        mobile: formData.whatsapp,
        profile: {
          full_name: formData.name,
          dob: formData.dob,
          birth_time: formData.tob,
          birth_place: formData.place,
          gender: formData.gender,
          mobile: formData.whatsapp,
        },
        lead_id: leadId,
        product_id: product?.id || null,
        service_id: product?.service_id || null,
        report_prefered_lang: formData.language || "Hindi",
        amount: totalAmount,
        remark: formData.remark || "",
        selected_addons: orderSnapshot,
        ...consultationData,
        is_profile_required: needsProfileFields || addonNeedsProfileFields,
        skip_otp: true, // Flag to indicate OTP should be skipped
        deviceContext,
        visitor_id: visitorId,  // first-party tracking
        utm_source: getQueryParam("utm_source"), // order_source tagged 'wa-agent' only when this matches, else stays NULL
      };

      console.log("🚀 Sending payload to /create-order-without-otp with deviceContext:", {
        hasDeviceContext: !!payload.deviceContext,
        deviceContextKeys: payload.deviceContext ? Object.keys(payload.deviceContext) : null,
      });

      const res = await postCreateOrderWithRetry(payload);

      if (res.data.status === "success") {
        const { order, user, token, leadId } = res.data.data;
        localStorage.setItem("userRefreshToken", token.refreshToken);
        localStorage.setItem("userAccessToken", token.accessToken);
        localStorage.setItem("user", JSON.stringify(res.data.data.user));
        setApiRole("user");
        setSuccessModal({
          show: true,
          message: "Order created successfully. Proceeding to payment...",
          title: "Order Created",
        });
        // Store order with complete form snapshot for comparison
        const initialFormSnapshot = JSON.stringify({
          name: formData.name,
          dob: formData.dob,
          tob: formData.tob,
          place: formData.place,
          gender: formData.gender,
          language: formData.language,
          remark: formData.remark,
          selectedAddons: formData.selectedAddons.sort(),
          consultation_date: formData.consultation_date,
          consultation_slot: formData.consultation_slot,
          consultation_problem_type: formData.consultation_problem_type,
          consultation_language: formData.consultation_language,
          orderSnapshot: orderSnapshot,
          totalAmount: totalAmount
        });
        
        setExistingOrder({
          ...order,
          formSnapshot: initialFormSnapshot
        });

        // Link landing_visit → lead_id + order_id in one shot (non-blocking)
        if (visitorId) {
          publicApi.post('/landing/link', {
            visitor_id: visitorId,
            lead_id:    leadId,
            order_id:   order.orderId,
          }).catch(() => {}); // never block the payment flow
        }

        setTimeout(() => {
          setSuccessModal({ show: false, message: "", title: "Success" });
          navigate("/order-payment", {
            state: {
              orderId: order.orderId,
              userId: order.userId,
              amount: order.amt,
              leadId,
              customerName: formData.name,
              customerContact: formData.whatsapp,
              product,
              checkoutMode,
            },
          });
        }, 1000);
      } else {
        setFormError(res.data.message || "Failed to create order.");
      }
    } catch (err) {
      if (err.response && err.response.data && err.response.data.message) {
        setFormError(err.response.data.message);
      } else {
        setFormError("Failed to process request.");
      }
      // Log critical order creation error
      CriticalErrorHandler.reportFormError('Order Creation', err, {
        product_id: product?.id,
        mobile: formData.whatsapp
      });
    } finally {
      isSubmittingRef.current = false;
      setLoading(false);
    }
  };

  // REMOVED: rechargeResState, sendOTP, verifyOTP, startResendTimer, handleOTPVerify,
  // handleOTPResend, handleOTPClose, handlePayment (was lines 885-1994) — all moved to
  // CommonPaymentPage.jsx. handleSubmit above now navigates to /order-payment instead
  // of calling handlePayment inline.

  // Calculate total amount including selected addons
  const totalAmount = React.useMemo(() => {
    if (!product) return 0;
    const basePrice = parseFloat(product.price) || 0;
    const addonsTotal = formData.selectedAddons.reduce((sum, addonId) => {
      const addon = product.available_addons?.find(a => a.id === addonId);
      return sum + (addon ? parseFloat(addon.price) : 0);
    }, 0);
    return basePrice + addonsTotal;
  }, [product, formData.selectedAddons]);

  // Main product is itself a Consultation service
  const isConsultationProduct = React.useMemo(
    () => product?.service === 'Consultation',
    [product]
  );

  // A Consultation addon has been ticked
  const hasConsultationAddon = React.useMemo(() => {
    if (!product?.available_addons) return false;
    return product.available_addons.some(
      (a) => formData.selectedAddons.includes(a.id) && a.service === 'Consultation'
    );
  }, [product, formData.selectedAddons]);

  // Consultation date/slot/problem/language are collected in either case.
  // Delivery routes on the product's own service name and ignores item_type, so a
  // Consultation main product books exactly like a Consultation addon does.
  const needsConsultationFields = isConsultationProduct || hasConsultationAddon;

  // Label shown in the "For: ..." header of the consultation box
  const consultationItemName = React.useMemo(() => {
    if (!product) return "";
    if (isConsultationProduct) return product.name;
    return (
      product.available_addons?.find(
        (a) => formData.selectedAddons.includes(a.id) && a.service === 'Consultation'
      )?.name || ""
    );
  }, [product, isConsultationProduct, formData.selectedAddons]);

  // Check if main product requires profile fields
  const needsProfileFields = React.useMemo(() => {
    if (!product) return false;
    // Consultations always need birth details, matching addonNeedsProfileFields below
    return product.profile_required === 1 || product.service === 'Consultation';
  }, [product]);

  // Check if selected addons need profile fields (and main product doesn't already show them)
  const addonNeedsProfileFields = React.useMemo(() => {
    if (!product) return false;
    // Main form already has them
    if (product.profile_required === 1 || product.service === 'Consultation') return false;
    return formData.selectedAddons.some(addonId => {
      const addon = product.available_addons?.find(a => a.id === addonId);
      return addon && (addon.profile_required === 1 || addon.service === 'Report' || addon.service === 'Consultation');
    });
  }, [product, formData.selectedAddons]);

  // ── Slot availability, all anchored to IST server time ──
  const slotOptions = React.useMemo(
    () => availableSlots(formData.consultation_date, effectiveNowMs),
    [formData.consultation_date, effectiveNowMs]
  );
  const minConsultationDate = React.useMemo(
    () => firstBookableDate(effectiveNowMs),
    [effectiveNowMs]
  );
  const maxConsultationDate = React.useMemo(
    () => lastBookableDate(effectiveNowMs),
    [effectiveNowMs]
  );
  const todaySlotsClosed = React.useMemo(
    () => isTodayClosed(effectiveNowMs),
    [effectiveNowMs]
  );

  // Default to the first bookable day so the customer isn't forced to open a
  // date picker before any slots appear.
  useEffect(() => {
    if (!needsConsultationFields) return;
    if (formData.consultation_date) return;
    setFormData((prev) => ({ ...prev, consultation_date: minConsultationDate }));
  }, [needsConsultationFields, formData.consultation_date, minConsultationDate]);

  // Today closing mid-session leaves the chosen date in the past — pull it
  // forward and drop the slot that went with it.
  useEffect(() => {
    if (!formData.consultation_date) return;
    if (formData.consultation_date >= minConsultationDate) return;
    setFormData((prev) => ({
      ...prev,
      consultation_date: minConsultationDate,
      consultation_slot: null,
    }));
    setSlotNotice("Those slots have passed. Please pick a time again.");
  }, [formData.consultation_date, minConsultationDate]);

  // Drop a selected slot once it falls inside the lead-time window.
  useEffect(() => {
    if (!formData.consultation_slot) return;
    if (slotOptions.some((slot) => slot.value === formData.consultation_slot)) return;
    setFormData((prev) => ({ ...prev, consultation_slot: null }));
    setSlotNotice("That time slot is no longer available. Please choose another.");
  }, [slotOptions, formData.consultation_slot]);

  // Ref for Full Name input (must be before any early return)
  const fullNameRef = React.useRef();

  if (productError) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
          padding: "20px",
        }}
      >
        <div
          style={{
            backgroundColor: "#fff",
            borderRadius: "12px",
            boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
            padding: "40px",
            textAlign: "center",
            maxWidth: "500px",
            border: "1px solid #f0f0f0",
          }}
        >
          <div
            style={{
              width: "60px",
              height: "60px",
              borderRadius: "50%",
              backgroundColor: "#fff3e0",
              margin: "0 auto 24px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "3px solid #ffcc80",
            }}
          >
            <span style={{ fontSize: "24px", color: "#f57c00" }}>⚠️</span>
          </div>
          <h2
            style={{
              color: "#3E2723",
              fontSize: "24px",
              fontWeight: "600",
              margin: "0 0 16px 0",
              fontFamily: "inherit",
            }}
          >
            Product currently unavailable. 
          </h2>
          <p
            style={{
              color: "#666",
              fontSize: "16px",
              lineHeight: "1.5",
              margin: "0 0 32px 0",
              fontFamily: "inherit",
            }}
          >
            {productError}
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              backgroundColor: "#f57c00",
              color: "white",
              border: "none",
              borderRadius: "6px",
              padding: "12px 24px",
              fontSize: "16px",
              fontWeight: "500",
              cursor: "pointer",
              transition: "all 0.2s ease",
              fontFamily: "inherit",
            }}
            onMouseOver={(e) => (e.target.style.backgroundColor = "#e65100")}
            onMouseOut={(e) => (e.target.style.backgroundColor = "#f57c00")}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!product)
    return (
      <div
        style={{
          padding: 40,
          textAlign: "center",
          fontSize: 22,
          color: "#888",
        }}
      >
        Loading...
      </div>
    );

  // Fallback image if not present
  const imageUrl = product?.image || productImage;
  // --- Image & Video Slider State ---
  // Safe images array
  const landingImages = getLandingImages(product).images;
  const images = landingImages.length > 0 ? landingImages : [imageUrl];

  // Swipe gesture handlers
  // ...existing code...
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };
  const handleTouchEnd = () => {
    if (touchStartX.current !== null && touchEndX.current !== null) {
      const delta = touchEndX.current - touchStartX.current;
      if (Math.abs(delta) > 40) {
        if (delta < 0) {
          // Swipe left: next image
          setCurrentImage((prev) => (prev + 1) % images.length);
        } else {
          // Swipe right: previous image
          setCurrentImage((prev) => (prev - 1 + images.length) % images.length);
        }
      }
    }
    touchStartX.current = null;
    touchEndX.current = null;
  };

  // Mouse drag handlers (desktop)
  const handleMouseDown = (e) => {
    mouseDownX = e.clientX;
  };
  const handleMouseUp = (e) => {
    mouseUpX = e.clientX;
    if (mouseDownX !== null && mouseUpX !== null) {
      const delta = mouseUpX - mouseDownX;
      if (Math.abs(delta) > 40) {
        if (delta < 0) {
          setCurrentImage((prev) => (prev + 1) % images.length);
        } else {
          setCurrentImage((prev) => (prev - 1 + images.length) % images.length);
        }
      }
    }
    mouseDownX = null;
    mouseUpX = null;
  };

  // Scroll and focus to Full Name input
  const handleOrderNowClick = () => {
    if (fullNameRef.current) {
      fullNameRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      setTimeout(() => fullNameRef.current.focus(), 400);
    }
  };

  return (
    <div className={styles.container}>
      {loading && <SpinnerOverlay message={loadingMessage} />}
      {/* REMOVED: payment/wallet spinners, ErrorModal, OTPModal — all moved to
          CommonPaymentPage.jsx along with the logic that drove them. */}
      <SuccessModal
        show={successModal.show}
        message={successModal.message}
        title={successModal.title}
        onClose={() =>
          setSuccessModal({ show: false, message: "", title: "Success" })
        }
      />
      {/* Trust Banner */}
      <div className={styles.trustBanner}>
        <span className={styles.trustItem}>
          <span className={styles.trustIcon}>⭐</span> 15+ years experience
        </span>
        <span className={styles.trustSeparator}>|</span>
        <span className={styles.trustItem}>
          <span className={styles.trustIcon}>📦</span> 50000+ reports delivered
        </span>
      </div>
      {/* Modern Hero Section */}
      <div className={styles.header}>
        <img
          src={require("../assets/images/vedic-wisdom-logo.png")}
          alt="Vedic Wisdom Logo"
          className={styles.logo}
          onMouseOver={(e) => (e.currentTarget.style.transform = "scale(1.05)")}
          onMouseOut={(e) => (e.currentTarget.style.transform = "scale(1)")}
        />
      </div>
      <h1 className={styles.title}>{product.tagline}</h1>
      <div className={styles.mainSection}>
        {/* Hero Banner */}
        <section className={styles.heroBanner}>
          <div className={styles.heroColumns}>
            <div className={styles.card}>
              {/* Image Gallery Component */}
             {/* Product Badge */}
              {product.badge && (
                <div className={styles.productBadge}>
                  <div className={styles.badgeBody}>
                    {product.badge}
                  </div>
                  <div className={styles.badgeArrow}>
                    <div className={styles.badgeArrowBorder}></div>
                  </div>
                </div>
              )}
              
              {/* Manual Image Slider for product images */}
              <div className={styles.heroImageSlider}>
                <img
                  src={
                    images?.[currentImage] ||
                    "https://via.placeholder.com/320x210?text=No+Image"
                  }
                  alt={product?.name || "Product image"}
                  className={styles.productImage}
                  fetchpriority="high"
                  loading="eager"
                  decoding="async"
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                  onMouseDown={handleMouseDown}
                  onMouseUp={handleMouseUp}
                  draggable={false}
                />

                {/* Dot indicators below main image */}
                {images.length > 1 && (
                  <div className={styles.sliderIndicators}>
                    {images.map((_, idx) => (
                      <span
                        key={idx}
                        className={
                          idx === currentImage
                            ? `${styles.indicatorDot} ${styles.activeDot}`
                            : styles.indicatorDot
                        }
                        onClick={() => setCurrentImage(idx)}
                      />
                    ))}
                  </div>
                )}
              </div>

              <h1
                style={{
                  fontSize: "1.3rem",
                  fontWeight: 800,
                  color: "#3E2723",
                  marginBottom: 18,
                  lineHeight: 1.15,
                }}
              >
                {product.name}
              </h1>
              
              {/* Product Labels */}
              {productLabels && productLabels.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
                  {productLabels.map((label, index) => {
                    const colorStyles = {
                      green: { backgroundColor: '#395c3a', color: 'white' },
                      blue: { backgroundColor: '#d9dbff', color: 'black' },
                      seagreen: { backgroundColor: '#2E8B57', color: 'black' },
                      skyblue: { backgroundColor: '#87CEEB', color: 'black' },
                      orange: { backgroundColor: '#a8720d', color: 'white' },
                      lightorange: { backgroundColor: '#FFDBBB', color: 'black' },
                      red: { backgroundColor: '#f77369', color: 'black' },
                      purple: { backgroundColor: '#d392df', color: 'black' },
                      teal: { backgroundColor: '#8bdad2', color: 'black' }
                    };
                    const style = colorStyles[label.label_color] || colorStyles.green;
                    
                    return (
                      <span
                        key={label.id || index}
                        style={{
                          ...style,
                          padding: '2px 4px',
                          borderRadius: 4,
                          fontSize: 12,
                          fontWeight: 300,
                          display: 'inline-block'
                        }}
                      >
                        {label.label_text}
                      </span>
                    );
                  })}
                </div>
              )}
              
              <div>
                <span className={styles.price}>₹{product.price} </span>
                {product.list_price && (
                  <span className={styles.listprice}>
                    ₹{product.list_price}
                  </span>
                )}

                <span className={styles.priceNote}>(incl. all taxes)</span>
              </div>
              {/* Mobile Order Now Button */}

              <button
                type="button"
                className={styles.mobileOrderBtn}
                style={{ width: "100%" }}
                onClick={handleOrderNowClick}
              >
                Order now your {product.service}
              </button>
              <div className={styles.reviewSection}>
                <div className={styles.stars}>
                  <span className={styles.starFilled}>★</span>
                  <span className={styles.starFilled}>★</span>
                  <span className={styles.starFilled}>★</span>
                  <span className={styles.starFilled}>★</span>
                  <span className={styles.starHalf}>★</span>
                </div>
                <span className={styles.rating}>4.9/5</span>
                <a 
                  href="#customer-reviews" 
                  className={styles.reviewCount}
                  onClick={(e) => {
                    e.preventDefault();
                    document.getElementById('customer-reviews')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  (8k+ reviews)
                </a>
              </div>
              <div className={styles.trustBadges}>
                <span className={styles.badge}>✅ Verified Service</span>
              </div>
              <ul className={styles.benefits}>
                <li>🔒 100% Privacy. Your data is never shared.</li>
                <li style={{ display: "flex", alignItems: "center" }}>
                  <FaWhatsapp
                    size={18}
                    style={{ color: "#25D366", marginRight: "8px" }}
                  />
                  Delivered via WhatsApp
                </li>
                <li>🛡️ Trusted by 50,000+ users</li>
                <li className={styles.reportLanguages}>
                  📝 Get Reports in
                  <div className={styles.languageContainer}>
                    {product?.profile_required !== 1 ? (
                      <>
                        <span
                          className={`${styles.languageTag} ${styles.languageTagSingle}`}
                        >
                          हिंदी
                        </span>
                        <span
                          className={`${styles.languageTag} ${styles.languageTagSingle}`}
                        >
                          English
                        </span>
                      </>
                    ) : (
                      [
                        "हिंदी",
                        "English",
                        "Marathi",
                        "Gujarati",
                        "Tamil",
                        "Telugu",
                        "Kannada",
                        "Bengali",  
                        "Punjabi",
                      ].map((lang) => (
                        <span key={lang} className={styles.languageTag}>
                          {lang}
                        </span>
                      ))
                    )}
                  </div>
                </li>
              </ul>
            </div>
            <div className={styles.card}>
              <form autoComplete="off" onSubmit={handleSubmit}>
                <div className={styles.formTitle}>
                  Get Your {product.service}
                  {(!product.available_addons || product.available_addons.length === 0) && (
                    <img
                      src={limitedoffer}
                      alt="limited-time-offer"
                      className={styles.limitedoffer}
                    />
                  )}
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    Full Name <span style={{ color: "#ff3d00" }}>*</span>
                  </label>
                  <input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={styles.formInput}
                    placeholder="Enter your full name"
                    required
                    ref={fullNameRef}
                    aria-label="Full Name"
                    aria-required="true"
                    aria-describedby={formError ? "form-error" : undefined}
                    autoComplete="off"
                  />
                </div>
                {needsProfileFields && (
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>
                      Date of Birth <span style={{ color: "#ff3d00" }}>*</span>
                    </label>
                    <DateOfBirthField
                      value={formData.dob}
                      onChange={handleChange}
                      required
                    />
                  </div>
                )}
                {needsProfileFields && (
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>
                      Time of Birth <span style={{ color: "#ff3d00" }}>*</span>
                    </label>
                    <TimePicker
                      name="tob"
                      value={formData.tob}
                      onChange={handleChange}
                      required
                    />
                    <UnknownBirthTimeCheckbox
                      checked={unknownTob}
                      onChange={handleUnknownTobChange}
                    />
                  </div>
                )}
                {needsProfileFields && (
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>
                      Place of Birth <span style={{ color: "#ff3d00" }}>*</span>
                    </label>
                    <PlaceOfBirthField
                      value={formData.place}
                      onChange={handlePlaceChange}
                      placeholder="Search for place, district, or state..."
                      onValidationError={handlePlaceValidationError}
                      required
                    />
                  </div>
                )}
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    WhatsApp Number <span style={{ color: "#ff3d00" }}>*</span>
                  </label>
                  <input
                    type="tel"
                    name="whatsapp"
                    value={formData.whatsapp}
                    onChange={handleChange}
                    placeholder="WhatsApp Number"
                    className={styles.formInput}
                    required
                    aria-label="WhatsApp Number"
                    aria-required="true"
                    aria-describedby="whatsapp-help"
                    autoComplete="off"
                  />
                  <div id="whatsapp-help" className="sr-only">
                    Enter your 10-digit WhatsApp number
                  </div>
                </div>
                {needsProfileFields && (
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>
                      Gender <span style={{ color: "#ff3d00" }}>*</span>
                    </label>
                    <select
                      name="gender"
                      value={formData.gender}
                      onChange={handleChange}
                      className={styles.formSelect}
                    >
                      <option value="">Select Gender</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                    </select>
                  </div>
                )}
                {/* Report language is meaningless for a consultation — the
                    Consultation Details box collects its own language */}
                {needsProfileFields && !isConsultationProduct && (
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>
                      Report Language/ भाषा{" "}
                    </label>
                    <select
                      name="language"
                      value={formData.language}
                      onChange={handleChange}
                      className={styles.formSelect}
                    >
                      <option value="Hindi"> हिंदी</option>
                      <option value="English">English</option>
                      <option value="Gujarati">Gujarati</option>
                      <option value="Punjabi">Punjabi</option>
                      <option value="Bengali">Bengali</option>
                      <option value="Telugu">Telugu</option>
                        <option value="Kannada">Kannada</option>
                      <option value="Tamil">Tamil</option>
                      <option value="Marathi">Marathi</option>
                    </select>
                  </div>
                )}
                
                {/* Custom Fields for Main Product - Display as regular form fields */}
                {product.user_input_fields && Array.isArray(product.user_input_fields) && product.user_input_fields.length > 0 && (
                  <>
                    {product.user_input_fields.map((field) => (
                      <div key={field.id} className={styles.formGroup}>
                        <label className={styles.formLabel}>
                          {field.label} {field.required && <span style={{ color: '#ff3d00' }}>*</span>}
                        </label>
                        
                        {field.type === 'text' && (
                          <input
                            type="text"
                            value={formData.customFields[field.id] || ''}
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              customFields: {
                                ...prev.customFields,
                                [field.id]: e.target.value
                              }
                            }))}
                            className={styles.formInput}
                            required={field.required}
                            maxLength={500}
                            placeholder={`Enter ${field.label.toLowerCase()}`}
                            autoComplete="off"
                          />
                        )}
                        
                        {field.type === 'textarea' && (
                          <textarea
                            value={formData.customFields[field.id] || ''}
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              customFields: {
                                ...prev.customFields,
                                [field.id]: e.target.value
                              }
                            }))}
                            className={styles.formInput}
                            required={field.required}
                            maxLength={500}
                            rows={4}
                            style={{ resize: 'vertical' }}
                            placeholder={`Enter ${field.label.toLowerCase()}`}
                            autoComplete="off"
                          />
                        )}
                        
                        {field.type === 'dropdown' && (
                          <select
                            value={formData.customFields[field.id] || ''}
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              customFields: {
                                ...prev.customFields,
                                [field.id]: e.target.value
                              }
                            }))}
                            className={styles.formSelect}
                            required={field.required}
                            style={{ width: '100%', boxSizing: 'border-box' }}
                          >
                            <option value="">Select an option</option>
                            {field.options && field.options.map((option, idx) => (
                              <option key={idx} value={option}>{option}</option>
                            ))}
                          </select>
                        )}
                      </div>
                    ))}
                  </>
                )}
                
                {/* Addon Products Section */}
                {product.available_addons && product.available_addons.length > 0 && (
                  <div className={styles.formGroup}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '12px',
                      marginBottom: '12px'
                    }}>
                      <label className={styles.formLabel} style={{ marginBottom: 0, fontSize: '22px', fontWeight: '700' }}>
                        Exclusive Offer for You
                      </label>
                      <img
                        src={limitedoffer}
                        alt="limited-time-offer"
                        style={{ 
                          maxWidth: '140px', 
                          height: 'auto',
                          display: 'block'
                        }}
                      />
                    </div>
                    <div style={{
                      backgroundColor: '#f9f6f2',
                      border: '2px solid #eee',
                      borderRadius: 8,
                      padding: '16px',
                      marginTop: 8
                    }}>
                      {product.available_addons.map((addon) => (
                        <div
                          key={addon.id}
                          style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            padding: '12px',
                            marginBottom: '8px',
                            backgroundColor: formData.selectedAddons.includes(addon.id) ? '#e8f5e9' : '#fff',
                            border: formData.selectedAddons.includes(addon.id) ? '2px solid #4caf50' : '1px solid #ddd',
                            borderRadius: 6,
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                          }}
                          onClick={() => {
                            setFormData(prev => ({
                              ...prev,
                              selectedAddons: prev.selectedAddons.includes(addon.id)
                                ? prev.selectedAddons.filter(id => id !== addon.id)
                                : [...prev.selectedAddons, addon.id]
                            }));
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={formData.selectedAddons.includes(addon.id)}
                            onChange={() => {
                              setFormData(prev => ({
                                ...prev,
                                selectedAddons: prev.selectedAddons.includes(addon.id)
                                  ? prev.selectedAddons.filter(id => id !== addon.id)
                                  : [...prev.selectedAddons, addon.id]
                              }));
                            }}
                            onClick={(e) => e.stopPropagation()}
                            style={{
                              marginRight: 12,
                              marginTop: 2,
                              width: 18,
                              height: 18,
                              cursor: 'pointer'
                            }}
                          />
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 4, color: '#333' }}>
                              {addon.name}
                            </div>
                            {(addon.service === 'Report' || addon.service === 'Consultation') && (
                              <div style={{ fontSize: 15, color: '#666', marginTop: 4, marginBottom: 6 }}>
                                Get customized remedies based on your kundli details.
                              </div>
                            )}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                              <span style={{ color: '#4caf50', fontSize: 16, fontWeight: 600 }}>
                                +₹{addon.price}
                              </span>
                              {addon.list_price && parseFloat(addon.list_price) > parseFloat(addon.price) && (
                                <span style={{ 
                                  color: '#999', 
                                  fontSize: 14, 
                                  textDecoration: 'line-through',
                                  fontWeight: 400
                                }}>
                                  ₹{addon.list_price}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                      {formData.selectedAddons.length > 0 && (
                        <div style={{
                          marginTop: 12,
                          padding: '10px 12px',
                          backgroundColor: '#e8f5e9',
                          borderRadius: 6,
                          fontSize: 14,
                          fontWeight: 600,
                          color: '#2e7d32'
                        }}>
                          Total Amount: ₹{(
                            parseFloat(product.price) +
                            formData.selectedAddons.reduce((sum, addonId) => {
                              const addon = product.available_addons.find(a => a.id === addonId);
                              return sum + (addon ? parseFloat(addon.price) : 0);
                            }, 0)
                          ).toFixed(2)}
                        </div>
                      )}
                    </div>

                    {/* Profile fields for addons (only if main product doesn't require profile) */}
                    {addonNeedsProfileFields && (
                      <div style={{ marginTop: 16 }}>
                        <label className={styles.formLabel} style={{ marginBottom: 12, display: 'block', color: '#ff6b35' }}>
                          📋 Birth Details Required for Selected Add-ons
                        </label>
                        <div style={{
                          backgroundColor: '#fff8f0',
                          border: '1px solid #ffd4a3',
                          borderRadius: 6,
                          padding: '16px',
                          marginBottom: 12,
                          boxSizing: 'border-box'
                        }}>
                          <div className={styles.formGroup} style={{ marginBottom: 14, width: '100%', boxSizing: 'border-box' }}>
                            <label className={styles.formLabel} style={{ fontSize: 14, display: 'block', marginBottom: 6 }}>
                              Date of Birth <span style={{ color: "#ff3d00" }}>*</span>
                            </label>
                            <DateOfBirthField
                              value={formData.dob}
                              onChange={handleChange}
                              required
                              style={{ width: '100%', boxSizing: 'border-box' }}
                            />
                          </div>

                          <div className={styles.formGroup} style={{ marginBottom: 14, width: '100%', boxSizing: 'border-box' }}>
                            <label className={styles.formLabel} style={{ fontSize: 14, display: 'block', marginBottom: 6 }}>
                              Time of Birth <span style={{ color: "#ff3d00" }}>*</span>
                            </label>
                            <TimePicker
                              name="tob"
                              value={formData.tob}
                              onChange={handleChange}
                              required
                            />
                            <UnknownBirthTimeCheckbox
                              checked={unknownTob}
                              onChange={handleUnknownTobChange}
                            />
                          </div>

                          <div className={styles.formGroup} style={{ marginBottom: 14, width: '100%', boxSizing: 'border-box' }}>
                            <label className={styles.formLabel} style={{ fontSize: 14, display: 'block', marginBottom: 6 }}>
                              Place of Birth <span style={{ color: "#ff3d00" }}>*</span>
                            </label>
                            <PlaceOfBirthField
                              value={formData.place}
                              onChange={handlePlaceChange}
                              placeholder="Search for place, district, or state..."
                              onValidationError={handlePlaceValidationError}
                              required
                            />
                          </div>

                          <div className={styles.formGroup} style={{ marginBottom: 14, width: '100%', boxSizing: 'border-box' }}>
                            <label className={styles.formLabel} style={{ fontSize: 14, display: 'block', marginBottom: 6 }}>
                              Gender <span style={{ color: "#ff3d00" }}>*</span>
                            </label>
                            <select
                              name="gender"
                              value={formData.gender}
                              onChange={handleChange}
                              className={styles.formSelect}
                              style={{ width: '100%', boxSizing: 'border-box' }}
                            >
                              <option value="">Select Gender</option>
                              <option value="male">Male</option>
                              <option value="female">Female</option>
                            </select>
                          </div>

                          <div className={styles.formGroup} style={{ marginBottom: 0, width: '100%', boxSizing: 'border-box' }}>
                            <label className={styles.formLabel} style={{ fontSize: 14, display: 'block', marginBottom: 6 }}>
                              Report Language / भाषा
                            </label>
                            <select
                              name="language"
                              value={formData.language}
                              onChange={handleChange}
                              className={styles.formSelect}
                              style={{ width: '100%', boxSizing: 'border-box' }}
                            >
                              <option value="Hindi"> हिंदी</option>
                              <option value="English">English</option>
                              <option value="Gujarati">Gujarati</option>
                              <option value="Punjabi">Punjabi</option>
                              <option value="Bengali">Bengali</option>
                              <option value="Telugu">Telugu</option>
                              <option value="Kannada">Kannada</option>
                              <option value="Tamil">Tamil</option>
                              <option value="Marathi">Marathi</option>
                            </select>
                          </div>
                        </div>
                      </div>
                    )}

                  </div>
                )}

                {/* Consultation details — rendered for a Consultation main product
                    or when a Consultation addon is selected. Lives outside the addon
                    block so it still shows when the product has no addons at all. */}
                {needsConsultationFields && (
                  <div style={{ marginTop: 16 }}>
                    <label className={styles.formLabel} style={{ marginBottom: 12, display: 'block' }}>
                      Consultation Details
                    </label>
                    <div style={{
                      backgroundColor: '#f0f4ff',
                      border: '1px solid #c5d9ff',
                      borderRadius: 6,
                      padding: 14,
                      marginBottom: 12
                    }}>
                      <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 10, color: '#1565c0' }}>
                        For: {consultationItemName}
                      </div>

                      {todaySlotsClosed && (
                        <div style={{
                          background: '#fff8e1',
                          border: '1px solid #ffe082',
                          borderRadius: 6,
                          padding: '10px 12px',
                          marginBottom: 12,
                          fontSize: 13,
                          color: '#8d6e00'
                        }}>
                          Today's slots are closed — showing the next available date.
                        </div>
                      )}

                      <div className={styles.formGroup} style={{ marginBottom: 10 }}>
                        <label className={styles.formLabel} style={{ fontSize: 13 }}>
                          Consultation Date <span style={{ color: "#ff3d00" }}>*</span>
                        </label>
                        <input
                          type="date"
                          value={formData.consultation_date || ''}
                          onChange={(e) => {
                            setSlotNotice("");
                            setFormData(prev => ({
                              ...prev,
                              consultation_date: e.target.value
                            }));
                          }}
                          className={styles.formInput}
                          min={minConsultationDate}
                          max={maxConsultationDate}
                          required
                        />
                      </div>

                      <div className={styles.formGroup} style={{ marginBottom: 10 }}>
                        <label className={styles.formLabel} style={{ fontSize: 13 }}>
                          Consultation Slot <span style={{ color: "#ff3d00" }}>*</span>
                        </label>
                        <select
                          value={formData.consultation_slot || ''}
                          onChange={(e) => {
                            setSlotNotice("");
                            setFormData(prev => ({
                              ...prev,
                              consultation_slot: e.target.value
                            }));
                          }}
                          className={styles.formSelect}
                          required
                        >
                          <option value="">Select Time Slot</option>
                          {slotOptions.map((slot) => (
                            <option key={slot.value} value={slot.value}>
                              {slotLabel(slot)}
                            </option>
                          ))}
                        </select>
                        {slotNotice && (
                          <div style={{ color: '#e65100', fontSize: 12, marginTop: 6 }}>
                            {slotNotice}
                          </div>
                        )}
                      </div>

                      <div className={styles.formGroup} style={{ marginBottom: 10 }}>
                        <label className={styles.formLabel} style={{ fontSize: 13 }}>
                          Problem Type <span style={{ color: "#ff3d00" }}>*</span>
                        </label>
                        <select
                          value={formData.consultation_problem_type || ''}
                          onChange={(e) => {
                            setFormData(prev => ({
                              ...prev,
                              consultation_problem_type: e.target.value
                            }));
                          }}
                          className={styles.formSelect}
                          required
                        >
                          <option value="">Select Problem Type</option>
                          <option value="Health">Health</option>
                          <option value="Career">Career</option>
                          <option value="Finance">Finance</option>
                          <option value="Couple Problem">Couple Problem</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>

                      <div className={styles.formGroup} style={{ marginBottom: 0 }}>
                        <label className={styles.formLabel} style={{ fontSize: 13 }}>
                          Consultation Language <span style={{ color: "#ff3d00" }}>*</span>
                        </label>
                        <select
                          value={formData.consultation_language || ''}
                          onChange={(e) => {
                            setFormData(prev => ({
                              ...prev,
                              consultation_language: e.target.value
                            }));
                          }}
                          className={styles.formSelect}
                          required
                        >
                          <option value="">Select Language</option>
                          <option value="Hindi">Hindi</option>
                          <option value="English">English</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}
                
                {product.askremark && (
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>
                      {product.askremark}
                    </label>
                    <textarea
                      name="remark"
                      value={formData.remark}
                      onChange={handleChange}
                      className={styles.formInput}
                      placeholder="Enter any remark (optional)"
                      rows={2}
                      maxLength={240}
                      style={{ resize: "none" }}
                    />
                    <div
                      style={{
                        fontSize: "0.8rem",
                        color: "#888",
                        textAlign: "right",
                        marginTop: 2,
                      }}
                    >
                      {remarkCharCount}/240
                    </div>
                  </div>
                )}
                
                {/* Custom Fields for Selected Addons - Display BELOW addon section */}
                {formData.selectedAddons.length > 0 && product.available_addons && 
                  product.available_addons
                    .filter(addon => formData.selectedAddons.includes(addon.id) && addon.user_input_fields && Array.isArray(addon.user_input_fields) && addon.user_input_fields.length > 0)
                    .map(addon => (
                      <div key={addon.id} style={{ marginTop: 20, padding: '16px', backgroundColor: '#f0f8ff', borderRadius: 8, border: '1px solid #b3d9ff' }}>
                        <div style={{ fontWeight: 600, marginBottom: 12, fontSize: 16, color: '#0066cc' }}>
                          Additional Information for {addon.name}
                        </div>
                        {addon.user_input_fields.map((field) => (
                          <div key={field.id} className={styles.formGroup}>
                            <label className={styles.formLabel}>
                              {field.label} {field.required && <span style={{ color: '#ff3d00' }}>*</span>}
                            </label>
                            
                            {field.type === 'text' && (
                              <input
                                type="text"
                                value={formData.addonCustomFields[addon.id]?.[field.id] || ''}
                                onChange={(e) => setFormData(prev => ({
                                  ...prev,
                                  addonCustomFields: {
                                    ...prev.addonCustomFields,
                                    [addon.id]: {
                                      ...prev.addonCustomFields[addon.id],
                                      [field.id]: e.target.value
                                    }
                                  }
                                }))}
                                className={styles.formInput}
                                required={field.required}
                                maxLength={500}
                                placeholder={`Enter ${field.label.toLowerCase()}`}
                                autoComplete="off"
                              />
                            )}
                            
                            {field.type === 'textarea' && (
                              <textarea
                                value={formData.addonCustomFields[addon.id]?.[field.id] || ''}
                                onChange={(e) => setFormData(prev => ({
                                  ...prev,
                                  addonCustomFields: {
                                    ...prev.addonCustomFields,
                                    [addon.id]: {
                                      ...prev.addonCustomFields[addon.id],
                                      [field.id]: e.target.value
                                    }
                                  }
                                }))}
                                className={styles.formInput}
                                required={field.required}
                                maxLength={500}
                                rows={4}
                                style={{ resize: 'vertical' }}
                                placeholder={`Enter ${field.label.toLowerCase()}`}
                                autoComplete="off"
                              />
                            )}
                            
                            {field.type === 'dropdown' && (
                              <select
                                value={formData.addonCustomFields[addon.id]?.[field.id] || ''}
                                onChange={(e) => setFormData(prev => ({
                                  ...prev,
                                  addonCustomFields: {
                                    ...prev.addonCustomFields,
                                    [addon.id]: {
                                      ...prev.addonCustomFields[addon.id],
                                      [field.id]: e.target.value
                                    }
                                  }
                                }))}
                                className={styles.formSelect}
                                required={field.required}
                                style={{ width: '100%', boxSizing: 'border-box' }}
                              >
                                <option value="">Select an option</option>
                                {field.options && field.options.map((option, idx) => (
                                  <option key={idx} value={option}>{option}</option>
                                ))}
                              </select>
                            )}
                          </div>
                        ))}
                      </div>
                    ))
                }
                
                {formError && (
                  <div
                    className={styles.formError}
                    id="form-error"
                    role="alert"
                    aria-live="polite"
                  >
                    {formError}
                  </div>
                )}
                <div className={styles.formGroup}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      background: "#f9f6f2",
                      borderRadius: 8,
                      padding: "12px 16px",
                      border: termsError
                        ? "3px solid #ff3d00"
                        : "2px solid #eee",
                      boxShadow: termsError
                        ? "0 0 0 6px #ffd180"
                        : "0 1px 4px rgba(0,0,0,0.04)",
                      transition: "border 0.2s, box-shadow 0.2s",
                      animation: termsError ? "shake 0.3s" : "none",
                    }}
                  >
                    {/* Shake animation keyframes */}
                    {termsError && (
                      <style>{`
                        @keyframes shake {
                          0% { transform: translateX(0); }
                          25% { transform: translateX(-4px); }
                          50% { transform: translateX(4px); }
                          75% { transform: translateX(-4px); }
                          100% { transform: translateX(0); }
                        }
                      `}</style>
                    )}
                    <input
                      type="checkbox"
                      id="termsCheckbox"
                      checked={formData.termsAccepted || false}
                      onChange={(e) => {
                        setFormData((prev) => ({
                          ...prev,
                          termsAccepted: e.target.checked,
                        }));
                        if (e.target.checked) setTermsError("");
                      }}
                      required
                      aria-required="true"
                      aria-describedby={
                        termsError ? "terms-error" : "terms-label"
                      }
                      style={{
                        accentColor: "#f57c00",
                        width: 12,
                        height: 12,
                        marginRight: 6,
                      }}
                    />
                    <label
                      htmlFor="termsCheckbox"
                      id="terms-label"
                      style={{
                        fontWeight: 500,
                        fontSize: "0.6rem",
                        color: "#3E2723",
                        margin: 0,
                        cursor: "pointer",
                      }}
                    >
                      I have read and accept the
                      <a
                        href="/terms-and-condition"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: "#f57c00",
                          textDecoration: "underline",
                          fontWeight: 700,
                          fontSize: "0.6rem",
                          marginLeft: 6,
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open("/terms-and-condition", "_blank");
                        }}
                      >
                        Terms & Conditions
                      </a>
                    </label>
                  </div>
                  {termsError && (
                    <div
                      id="terms-error"
                      role="alert"
                      aria-live="assertive"
                      style={{
                        color: "#f57c00",
                        fontWeight: 600,
                        marginTop: 6,
                        fontSize: 15,
                      }}
                    >
                      {termsError}
                    </div>
                  )}
                </div>
                
                <button
                  type="submit"
                  className={styles.submitBtn}
                  disabled={loading}
                  aria-describedby={loading ? "loading-status" : undefined}
                >
                  {loading
                    ? "Processing..."
                    : `Get ${product.service} @₹${totalAmount.toFixed(2)}`}
                </button>
                {loading && (
                  <div
                    id="loading-status"
                    className="sr-only"
                    aria-live="polite"
                  >
                    Processing your order, please wait
                  </div>
                )}
              </form>
            </div>
          </div>
        </section>
        {/* Description Section */}
        <section className={styles.descriptionSection}>
          <h2 className={styles.descriptionTitle}>About this item</h2>
          <div
            className={styles.descriptionText}
            dangerouslySetInnerHTML={{ __html: product.description }}
          />
        </section>
        {product?.video_url && (
          <section className={styles.descriptionSection}>
            <h2 className={styles.descriptionTitle}>Connect more</h2>
            <VideoPlayer
              videoUrl={product?.video_url}
              thumbnail={product?.video_thumbnail || thumbnail}
            />
          </section>
        )}
        
        {/* Product Reviews Section */}
        {productReviews.length > 0 && (
          <section id="customer-reviews" className={styles.reviewsSection}>
            <h2 className={styles.reviewsSectionTitle}>Feedback from our users</h2>
            <div className={styles.reviewsList}>
              {productReviews.slice(0, 3).map((review) => (
                <div key={review.id} className={styles.reviewItem}>
                  <p className={styles.reviewText}>{review.review_text}</p>
                  <div className={styles.reviewFooter}>
                    <div className={styles.userProfileImage}>
                      {review.customer_name.charAt(0).toUpperCase()}
                    </div>
                    <div className={styles.userInfo}>
                      <strong className={styles.customerName}>{review.customer_name}</strong>
                      <div className={styles.reviewStars}>
                        {[1, 2, 3, 4, 5].map(star => (
                          <span key={star} className={star <= review.rating ? styles.starFilled : styles.starEmpty}>
                            ★
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {productReviews.length > 3 && (
              <button 
                className={styles.showMoreBtn}
                onClick={() => setShowReviewModal(true)}
              >
                Read More Reviews <span className={styles.arrowRight}>›</span>
              </button>
            )}
          </section>
        )}
        
        {/* Frequently Asked Questions Section */}
        <section className={styles.faqSection}>
          <h2 className={styles.faqSectionTitle}>Frequently Asked Questions</h2>
          <FAQ faqs={[
            {
              question: "How will I receive my kundli report?",
              answer: "Your kundli report will be delivered directly to your WhatsApp number within 24 hours of order confirmation. You will receive a detailed PDF document that you can download and save for future reference."
            },
            {
              question: "What if I don't know my birth time?",
              answer: "Accurate birth time helps to create more accurate report. However we are still able to prepare 80% accurate report if you don't know your exact time of birth"
            },
            {
              question: "Is my personal information secure?",
              answer: "Yes, absolutely! We take data privacy very seriously. Your personal information is encrypted and stored securely. We never share your data with third parties. All information is used solely for preparing your astrological report."
            },
            {
              question: "How accurate are the predictions?",
              answer: "Our predictions are based on Vedic astrology principles with over 15 years of experience. While we strive for maximum accuracy, astrology is a guiding science and actual outcomes may vary based on individual circumstances and free will."
            },
            {
              question: "Can I order for someone else?",
              answer: "Yes, you can order a kundli report for family members or friends. Just enter their birth details when placing the order. The report will be delivered to the WhatsApp number you provide during checkout."
            }
          ]} />
        </section>
        
        {/* Footer Section */}
        <footer className={styles.footer}>
          <div className={styles.footerContent}>
            <h3 className={styles.footerTitle}>Contact Us</h3>
            <p className={styles.footerText}>
              Email: <a href="mailto:support@astroshubh.com" className={styles.footerLink}>support@astroshubh.com</a>
            </p>
          </div>
        </footer>
      </div>
      
      {/* Review Modal */}
      {showReviewModal && (
        <ReviewModal 
          reviews={productReviews}
          onClose={() => setShowReviewModal(false)}
        />
      )}
      
      {/* Recent Orders Toast Notifications */}
      {showToast && recentOrders[currentToastIndex] && (
        <RecentOrderToast
          name={recentOrders[currentToastIndex].name}
          secondsAgo={recentOrders[currentToastIndex].secondsAgo}
          onClose={handleToastClose}
        />
      )}

      {/* REMOVED: FallbackPaymentModal — moved to CommonPaymentPage.jsx along with the
          rest of the payment-failure handling that drove it. */}
    </div>
  );
};
export default LandingPage;
